/**
 * GateEscopoScreen — Quality Gate 4 · Escopo (gate de FASE, fim da Fase 5).
 *
 * As personas de gestão (Gerente de Projetos · Scrum Master) validam o Product
 * Backlog, as dependências e o plano de ciclos antes de liberar a execução.
 * Fiel ao protótipo (GateEscopoScreen · GATE_DEFS.escopo): pills + 4 cards
 * (Backlog resumido, Ciclos planejados com custo, Dependências, Estimativa total).
 * Lê tudo da memória/contexto do projeto (GET /backlog). Aprova via
 * PATCH /journey-gates/escopo.
 */
import { C } from '@/theme/palette';
import { useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useProjects } from '@/api/projects';
import { useBacklog } from '@/api/backlog';
import { useJourneyGate, useDecideJourneyGate } from '@/api/techScope';
import { BACKLOG_TYPE, backlogStats, leavesByCycle, effectivePoints, leafTokens, usd, TOKEN_RATE } from '../backlog/backlogModel';
import { GateExportBar } from './GateExportBar';

// Paleta: segue o tema global (aliases bg/surface/border/text/dim/muted… em theme/palette.js).

const GATE_DEF = {
  num: 4, label: 'Gate de Escopo', sla: '24h',
  personas: 'Gerente de Projetos · Scrum Master',
  approvers: [
    { name: 'Rafael Pinto', role: 'Gerente de Projetos', avatar: 'RP' },
    { name: 'Juliana Castro', role: 'Scrum Master', avatar: 'JC' },
  ],
};

const ST = {
  approved: { label: 'APROVADO', fg: C.ok, bg: 'rgba(1,230,228,0.12)' },
  rejected: { label: 'REJEITADO', fg: C.bad, bg: 'rgba(255,51,0,0.12)' },
  pending:  { label: 'AGUARDANDO APROVAÇÃO', fg: C.warn, bg: 'rgba(255,176,32,0.12)' },
};

function Card({ title, right, accent = C.cyber, children }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, background: C.surface, borderRadius: 12, padding: 16, marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <h4 style={{ margin: 0, fontFamily: 'monospace', fontSize: 11, textTransform: 'uppercase',
          letterSpacing: '0.1em', color: accent }}>{title}</h4>
        {right && <span style={{ marginLeft: 'auto', fontSize: 11.5, color: C.muted }}>{right}</span>}
      </div>
      {children}
    </div>
  );
}

function Pill({ label, value }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 14px',
      border: `1px solid ${C.border}`, borderRadius: 10, background: C.surface }}>
      <span style={{ fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.muted }}>{label}</span>
      <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{value}</span>
    </div>
  );
}

export function GateEscopoScreen({ projectId: propProjectId, onContinue, onBack } = {}) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlProject = searchParams.get('project');
  const { data: projectsRaw } = useProjects();
  const projects = useMemo(
    () => (Array.isArray(projectsRaw) ? projectsRaw : projectsRaw?.items ?? []),
    [projectsRaw],
  );
  const projectId = propProjectId || urlProject || projects?.[0]?.id || null;

  const { data: gate } = useJourneyGate(projectId, 'escopo');
  const decide = useDecideJourneyGate(projectId, 'escopo');
  const { data: backlog } = useBacklog(projectId);
  const [comment, setComment] = useState('');

  const epics = backlog?.epics ?? [];
  const cycles = backlog?.cycles ?? [];
  const stats = useMemo(() => backlogStats(epics), [epics]);
  const byCycle = useMemo(() => leavesByCycle(epics), [epics]);

  if (!projectId) return <div style={{ padding: 40, color: C.dim }}>Nenhum projeto selecionado.</div>;

  const status = gate?.status ?? 'pending';
  const s = ST[status] || ST.pending;
  const approved = status === 'approved';

  const goDesigner = () => navigate(`/journey?project=${projectId}`);
  const goJourney = () => (onContinue ? onContinue() : navigate(`/project?project=${projectId}`));

  const cyclePlan = cycles.map((c) => {
    const items = byCycle.get(c.number) || [];
    const pts = items.reduce((a, l) => a + effectivePoints(l), 0);
    const tokens = items.reduce((a, l) => a + leafTokens(l), 0);
    return { ...c, count: items.length, pts, tokens, cost: tokens * TOKEN_RATE };
  });

  // ── objeto normalizado para exportação (.md / PDF) — GateExportBar ─────────
  const exportGate = {
    num: GATE_DEF.num,
    label: GATE_DEF.label,
    projectId,
    description: 'A persona de gestão valida o Product Backlog, as dependências e o plano '
      + 'de ciclos antes de liberar a execução. O backlog é integralmente detalhado antes '
      + 'desta aprovação — os ciclos executam de forma auto-encadeada, sem re-aprovação humana.',
    status,
    statusLabel: s.label,
    decidedAt: gate?.decidedAt,
    comment: gate?.comment,
    sla: GATE_DEF.sla,
    personas: GATE_DEF.personas,
    approvers: GATE_DEF.approvers.map((a) => ({ ...a, done: approved })),
    sections: [
      epics.length ? {
        title: 'Product Backlog resumido',
        kind: 'table',
        headers: ['Épico', 'Ciclo', 'Itens', 'Pontos'],
        rows: epics.map((ep) => {
          const leaves = (ep.features || []).flatMap((f) => f.stories || []);
          const pts = leaves.reduce((a, l) => a + effectivePoints(l), 0);
          return [ep.title, ep.cycleNumber ?? '—', leaves.length, pts];
        }),
      } : null,
      cyclePlan.length ? {
        title: 'Ciclos planejados · Sprint Backlogs encadeados',
        kind: 'table',
        headers: ['Ciclo', 'Tema', 'Itens', 'Pontos', 'Custo'],
        rows: cyclePlan.map((c) => [
          `Ciclo ${c.number}`,
          c.number === 0 ? 'Fundação Técnica' : c.theme,
          c.count, c.pts, usd(c.cost),
        ]),
      } : null,
      stats.withDeps.length ? {
        title: 'Dependências e pré-requisitos',
        kind: 'list',
        items: stats.withDeps.map((l) => `${l.title} — depende de: ${(l.deps || []).join(', ')}`),
      } : null,
      {
        title: 'Estimativa total da execução',
        kind: 'kv',
        data: [
          ['Tokens estimados', `~${Math.round(stats.totalTokens).toLocaleString('pt-BR')}`],
          ['Custo estimado', usd(stats.totalCost)],
          ['Prazo', `${cycles.length} ciclos · ~9 dias úteis`],
          ['Sprint Backlogs', String(cycles.length)],
        ],
      },
    ].filter(Boolean),
  };

  return (
    <div className="sp-page" style={{ color: C.text, maxWidth: 1000 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 22, letterSpacing: '-0.02em' }}>
          🛡 Quality Gate {GATE_DEF.num} — {GATE_DEF.label}
        </h2>
        <span style={{ fontFamily: 'monospace', fontSize: 11, padding: '3px 9px', borderRadius: 6,
          color: s.fg, background: s.bg, letterSpacing: '0.04em' }}>{s.label}</span>
        <GateExportBar gate={exportGate} style={{ marginLeft: 'auto' }} />
      </div>
      <p style={{ color: C.dim, marginTop: 8, fontSize: 13.5, maxWidth: '85ch' }}>
        A persona de gestão valida o Product Backlog, as dependências e o plano de ciclos antes de
        liberar a execução. O backlog é integralmente detalhado antes desta aprovação — os ciclos
        executam de forma auto-encadeada, sem re-aprovação humana; intervenção apenas por exceção.
      </p>

      {/* Pills */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
        <Pill label="Épicos" value={String(stats.epics || '—')} />
        <Pill label="Itens" value={String(stats.leaves.length || '—')} />
        <Pill label="Story points" value={String(stats.totalPts || '—')} />
        <Pill label="Ciclos" value={cycles.length ? `${cycles.length} encadeados` : '—'} />
      </div>

      {gate?.decidedAt && (
        <p style={{ color: C.muted, fontSize: 12.5, marginTop: 12 }}>
          Decidido em {new Date(gate.decidedAt).toLocaleString('pt-BR')}
          {gate.comment ? ` · ${gate.comment}` : ''}
        </p>
      )}
      {decide.isError && (
        <div style={{ marginTop: 12, color: C.bad, fontSize: 13 }}>
          {String(decide.error?.title || decide.error?.message || decide.error)}
        </div>
      )}

      {stats.leaves.length === 0 && (
        <div style={{ marginTop: 16, padding: 20, border: `1px dashed ${C.border}`, borderRadius: 12, color: C.muted }}>
          Sem backlog salvo ainda — volte ao Product Backlog, gere/ajuste e confirme antes de aprovar o escopo.
        </div>
      )}

      {/* 1 · Product Backlog resumido */}
      <Card title="Product Backlog resumido" accent={C.quantum}
        right={`${stats.epics} épicos · ${stats.stories} histórias · ${stats.tasks} atividades técnicas`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {epics.map((ep) => {
            const leaves = (ep.features || []).flatMap((f) => f.stories || []);
            const pts = leaves.reduce((a, l) => a + effectivePoints(l), 0);
            return (
              <div key={ep.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px',
                background: C.black, borderRadius: 8, border: `1px solid ${C.border}` }}>
                <span style={{ fontFamily: 'monospace', fontSize: 9.5, padding: '2px 7px', borderRadius: 6,
                  color: BACKLOG_TYPE.epic.color, background: `${BACKLOG_TYPE.epic.color}1F` }}>ÉPICO</span>
                <span style={{ fontSize: 13, color: C.text, flex: 1 }}>{ep.title}</span>
                {ep.cycleNumber != null && (
                  <span style={{ fontFamily: 'monospace', fontSize: 10, color: C.muted }}>C{ep.cycleNumber}</span>
                )}
                <span style={{ fontFamily: 'monospace', fontSize: 11, color: C.dim }}>{leaves.length} itens · {pts} pts</span>
              </div>
            );
          })}
        </div>
      </Card>

      {/* 2 · Ciclos planejados */}
      <Card title="Ciclos planejados · Sprint Backlogs encadeados">
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6 }}>
          {cyclePlan.map((c, i) => (
            <div key={c.number} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ minWidth: 160, background: C.black, border: `1px solid ${C.border}`,
                borderTop: `3px solid ${c.number === 0 ? C.quantum : C.cyber}`, borderRadius: 9, padding: 11 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: C.text }}>Ciclo {c.number}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{c.number === 0 ? 'Fundação Técnica' : c.theme}</div>
                <div style={{ fontSize: 11, color: C.dim, marginTop: 6 }}>{c.count} itens · {c.pts} pts</div>
                <div style={{ fontSize: 11, color: C.cyber, marginTop: 2 }}>{usd(c.cost)}</div>
              </div>
              {i < cyclePlan.length - 1 && <span style={{ color: C.muted, fontSize: 16 }}>›</span>}
            </div>
          ))}
        </div>
      </Card>

      {/* 3 · Dependências e pré-requisitos */}
      <Card title="Dependências e pré-requisitos" accent={C.digital}
        right={`${stats.withDeps.length} itens com dependência · ${violationsOf(stats, byCycle)} violações de ordem`}>
        {stats.withDeps.length === 0 ? (
          <div style={{ fontSize: 12.5, color: C.muted }}>Nenhuma dependência cruzada mapeada.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {stats.withDeps.slice(0, 6).map((l) => (
              <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                padding: '8px 10px', background: C.black, borderRadius: 8, border: `1px solid ${C.border}` }}>
                <span style={{ color: C.digital }}>⑃</span>
                <span style={{ fontSize: 12.5, color: C.text }}>{l.title}</span>
                <span style={{ fontSize: 11, color: C.muted }}>depende de</span>
                {l.deps.map((d, i) => (
                  <span key={i} style={{ fontSize: 10.5, fontFamily: 'monospace', padding: '2px 8px', borderRadius: 6,
                    color: C.cyber, background: `${C.cyber}1A`, border: `1px solid ${C.cyber}44` }}>{String(d).slice(0, 28)}</span>
                ))}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* 4 · Estimativa total */}
      <Card title="Estimativa total da execução" accent={C.neon}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          <Est label="Tokens estimados" value={`~${Math.round(stats.totalTokens).toLocaleString('pt-BR')}`} />
          <Est label="Custo estimado" value={usd(stats.totalCost)} />
          <Est label="Prazo" value={`${cycles.length} ciclos · ~9 dias úteis`} />
          <Est label="Sprint Backlogs" value={String(cycles.length)} />
        </div>
      </Card>

      {/* Aprovadores + parecer + botões */}
      <div style={{ marginTop: 20, border: `1px solid ${C.border}`, background: C.surface2, borderRadius: 12, padding: 16 }}>
        {approved && (
          <div style={{ marginBottom: 14, padding: '10px 12px', borderRadius: 8,
            background: 'rgba(1,230,228,0.06)', border: `1px solid ${C.cyber}44`, fontSize: 12.5, color: C.dim }}>
            <strong style={{ color: C.cyber }}>Gate aprovado — fase concluída.</strong> Aprovações registradas
            na trilha de auditoria imutável (hash-chain).
          </div>
        )}
        <div style={{ fontSize: 10, letterSpacing: '0.05em', color: C.muted, fontWeight: 700, marginBottom: 8 }}>
          APROVADORES · {GATE_DEF.personas.toUpperCase()}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          {GATE_DEF.approvers.map((a) => (
            <div key={a.name} style={{ display: 'flex', alignItems: 'center', gap: 8,
              border: `1px solid ${C.border}`, borderRadius: 999, padding: '4px 12px 4px 4px', background: C.black }}>
              <span style={{ width: 26, height: 26, borderRadius: 999, background: approved ? C.cyber : C.surface2,
                color: approved ? C.black : C.dim, display: 'grid', placeItems: 'center',
                fontSize: 10, fontWeight: 700 }}>{a.avatar}</span>
              <span style={{ fontSize: 12 }}>
                <span style={{ color: C.text, fontWeight: 600 }}>{a.name}</span>
                <span style={{ color: C.muted }}> · {a.role}</span>
              </span>
              {approved && <span style={{ color: C.ok, fontSize: 12 }}>✓</span>}
            </div>
          ))}
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
          ⏱ SLA: aprovação em até {GATE_DEF.sla} · Escala para backup em 48h · Admin em 72h
        </div>

        <label style={{ fontSize: 12.5, color: C.dim }}>Parecer do aprovador (obrigatório para rejeitar)</label>
        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
          placeholder="Ex: antecipar uma história para o Ciclo anterior se a fundação estiver estável…"
          style={{ width: '100%', marginTop: 6, background: C.black, color: C.text,
            border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, fontSize: 13, resize: 'vertical' }} />

        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          {onBack && (
            <button onClick={onBack} style={{ background: 'transparent', color: C.dim,
              border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 16px', fontSize: 13,
              fontWeight: 600, cursor: 'pointer' }}>← Voltar</button>
          )}
          <button onClick={goDesigner} style={{ background: 'transparent', color: C.accent || C.cyber,
            border: `1px solid ${C.cyber}55`, borderRadius: 8, padding: '9px 16px', fontSize: 13, cursor: 'pointer' }}>
            Ajustar no Backlog
          </button>
          {approved ? (
            <button onClick={goJourney} style={{ background: C.cyber, color: C.black, border: 'none',
              borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              Iniciar Ciclo 0 no Live Board →
            </button>
          ) : (
            <>
              <button onClick={() => decide.mutate({ decision: 'approve', comment })} disabled={decide.isPending}
                style={{ background: C.cyber, color: C.black, border: 'none', borderRadius: 8,
                  padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: decide.isPending ? 0.6 : 1 }}>
                {decide.isPending ? 'Registrando…' : 'Aprovar escopo e iniciar Ciclo 0'}
              </button>
              <button onClick={() => decide.mutate({ decision: 'reject', comment })} disabled={decide.isPending}
                style={{ background: 'transparent', color: C.bad, border: `1px solid ${C.bad}`, borderRadius: 8,
                  padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Rejeitar</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Est({ label, value }) {
  return (
    <div style={{ padding: 12, background: C.black, border: `1px solid ${C.border}`, borderRadius: 9 }}>
      <div style={{ fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase', color: C.muted }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginTop: 4 }}>{value}</div>
    </div>
  );
}

// Conta dependências que violam a ordem dos ciclos (dep em ciclo posterior).
function violationsOf(stats, byCycle) {
  const cycleOfTitle = new Map();
  byCycle.forEach((items, cyc) => items.forEach((l) => { if (l.title) cycleOfTitle.set(String(l.title), cyc); }));
  let v = 0;
  stats.withDeps.forEach((l) => {
    (l.deps || []).forEach((d) => {
      const dc = cycleOfTitle.get(String(d));
      if (dc != null && l.cycle != null && dc > l.cycle) v += 1;
    });
  });
  return v;
}
